import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, MapPin, CreditCard, Package } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  placed: "#D4AF37",
  packed: "#D4AF37",
  shipped: "#0A1F44",
  on_rikshaw: "#E91E63",
  delivered: "#16a34a",
  cancelled: "#9ca3af",
  returned: "#9ca3af",
  payment_confirmed: "#16a34a",
  payment_failed: "#dc2626",
};

const friendly = (s) => (s || "").replaceAll("_", " ");

export default function OrderDetail() {
  const { order_no } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${order_no}`);
      setOrder(data);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Order not found." : "Could not load.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_no]);

  if (!localStorage.getItem("jow_token")) return <Navigate to="/login" replace />;

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="order-error">
        <h1 className="font-display text-3xl text-jow-navy">{error}</h1>
        <Link to="/account/orders" className="btn-secondary-jow mt-6 inline-flex">
          ← Back to orders
        </Link>
      </div>
    );
  }
  if (!order) return null;

  const cancellable = ["placed", "packed"].includes(order.status);

  const cancel = async () => {
    if (!window.confirm(`Cancel ${order.order_no}?`)) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_no}/cancel`);
      toast.success("Order cancelled.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid={`order-${order.order_no}`}>
      <Link to="/account/orders" className="label-overline text-jow-pink hover:underline inline-flex items-center gap-1">
        <ChevronLeft size={14} /> Back to orders
      </Link>

      <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="label-overline text-jow-gold">{order.payment.method.toUpperCase()}</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2 text-jow-navy">
            {order.order_no}
          </h1>
          <p className="text-sm text-jow-navy/55 mt-1">
            Placed {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <span
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border"
          style={{ borderColor: STATUS_COLORS[order.status] || "#0A1F44", color: STATUS_COLORS[order.status] || "#0A1F44" }}
          data-testid="order-status-pill"
        >
          <Package size={12} /> {friendly(order.status)}
        </span>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-8">
          <div className="bg-white border border-jow-navy/10 p-6">
            <p className="label-overline text-jow-navy mb-4">Items</p>
            <ul className="divide-y divide-jow-navy/10" data-testid="order-items">
              {order.items.map((i) => (
                <li key={i.sku} className="py-4 flex items-center gap-4" data-testid={`order-item-${i.sku}`}>
                  <img src={i.image} alt={i.name} className="w-16 h-16 object-cover" />
                  <div className="flex-1">
                    <p className="font-display text-base text-jow-navy">{i.name}</p>
                    <p className="text-xs text-jow-navy/55">
                      Qty {i.qty} · {i.attrs?.color}
                    </p>
                  </div>
                  <span className="text-sm">₹{i.line_total.toLocaleString("en-IN")}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-jow-navy/10 p-6">
            <p className="label-overline text-jow-navy mb-3 flex items-center gap-2">
              <MapPin size={14} /> Delivery address
            </p>
            <p className="text-sm text-jow-navy" data-testid="order-address">
              <strong>{order.address.label}</strong> · {order.address.name}
              <br />
              {order.address.line1}
              {order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city},{" "}
              {order.address.state} {order.address.pincode}
              <br />
              <span className="text-jow-navy/55 text-xs">{order.address.phone}</span>
            </p>
          </div>

          <div className="bg-white border border-jow-navy/10 p-6">
            <p className="label-overline text-jow-navy mb-3 flex items-center gap-2">
              <CreditCard size={14} /> Payment
            </p>
            <p className="text-sm text-jow-navy" data-testid="order-payment">
              Method: <strong>{order.payment.method.toUpperCase()}</strong> · Status:{" "}
              <strong style={{ color: STATUS_COLORS[order.payment.status] || "#0A1F44" }}>
                {friendly(order.payment.status)}
              </strong>
              {order.payment.mock_mode && (
                <span className="ml-2 text-jow-pink">(mock)</span>
              )}
            </p>
          </div>
        </div>

        <aside className="lg:col-span-5 space-y-6">
          <div className="bg-jow-cream border border-jow-gold/40 p-6">
            <p className="label-overline text-jow-gold mb-4">Bill</p>
            <Row label="Subtotal" value={`₹${order.subtotal.toLocaleString("en-IN")}`} testid="bill-subtotal" />
            {order.discount > 0 && (
              <Row
                label={`Discount${order.coupon?.code ? ` (${order.coupon.code})` : ""}`}
                value={<span className="text-jow-pink">−₹{order.discount.toLocaleString("en-IN")}</span>}
                testid="bill-discount"
              />
            )}
            <Row label="Shipping" value={order.shipping === 0 ? "Free" : `₹${order.shipping}`} testid="bill-shipping" />
            <Row label="GST" value={`₹${order.tax.toLocaleString("en-IN")}`} testid="bill-tax" />
            <div className="border-t border-jow-navy/10 mt-3 pt-3">
              <Row
                label={<span className="font-display text-lg">Total</span>}
                value={<span className="font-display text-lg">₹{order.total.toLocaleString("en-IN")}</span>}
                testid="bill-total"
              />
            </div>
          </div>

          <div className="bg-white border border-jow-navy/10 p-6">
            <p className="label-overline text-jow-navy mb-4">Timeline</p>
            <ol className="space-y-4" data-testid="order-timeline">
              {order.timeline.map((t, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex gap-3"
                  data-testid={`tl-${i}`}
                >
                  <span
                    className="w-3 h-3 rounded-full mt-1 shrink-0"
                    style={{ background: STATUS_COLORS[t.status] || "#0A1F44" }}
                  />
                  <div className="text-sm">
                    <p className="font-display text-jow-navy capitalize">{friendly(t.status)}</p>
                    {t.note && <p className="text-jow-navy/55 text-xs">{t.note}</p>}
                    <p className="text-jow-navy/40 text-[10px] mt-1">{new Date(t.at).toLocaleString()}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>

          {cancellable && (
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              className="btn-secondary-jow w-full text-jow-pink border-jow-pink hover:bg-jow-pink hover:text-white"
              data-testid="order-cancel-btn"
            >
              {busy ? "Cancelling…" : "Cancel order"}
            </button>
          )}

          {order.assigned_rikshaw_id && (
            <Link
              to={`/order/${order.order_no}/track`}
              className="btn-primary-jow w-full"
              data-testid="order-track-link"
            >
              Track on map →
            </Link>
          )}

          <p className="text-xs text-jow-navy/55 text-center">
            Need help? Email <a className="text-jow-pink" href="mailto:care@jewelleryonwheels.in">care@jewelleryonwheels.in</a>
          </p>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm py-1" data-testid={testid}>
      <span className="text-jow-navy/65">{label}</span>
      <span className="text-jow-navy">{value}</span>
    </div>
  );
}
